import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/auth";
import { Loader2, Save, Edit3, Package, ShoppingBag, Check, X } from "lucide-react";

const STATUS_OPTS = [
  { v: "pending",                    label: "En attente" },
  { v: "pending_payment_validation", label: "🟠 Paiement à vérifier" },
  { v: "payment_validated",          label: "✅ Paiement validé" },
  { v: "payment_rejected",           label: "❌ Paiement refusé" },
  { v: "paid",                       label: "Payé" },
  { v: "activated",                  label: "✓ Licence activée" },
  { v: "expired",                    label: "Expiré" },
  { v: "cancelled",                  label: "Annulé" },
];
const STATUS_COLOR = {
  pending: "text-amber-400 border-amber-500/40",
  pending_payment_validation: "text-amber-400 border-amber-500/40",
  payment_validated: "text-sky-400 border-sky-500/40",
  payment_rejected: "text-red-400 border-red-500/40",
  paid: "text-sky-400 border-sky-500/40",
  activated: "text-emerald-400 border-emerald-500/40",
  expired: "text-zinc-400 border-zinc-500/40",
  cancelled: "text-red-400 border-red-500/40",
};

function PriceCell({ tool, planKey, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(tool[`price_${planKey}`] ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await onSave(tool.id, { [`price_${planKey}`]: val === "" ? null : parseFloat(val) });
      setEditing(false);
    } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input type="number" step="0.01" min="0" value={val}
          onChange={e => setVal(e.target.value)}
          className="input-lhs !py-1 !text-xs w-20"
          data-testid={`price-input-${tool.slug}-${planKey}`} />
        <button onClick={save} disabled={busy} className="text-emerald-400"><Check className="w-4 h-4" /></button>
        <button onClick={() => setEditing(false)} className="text-zinc-500"><X className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <button onClick={() => setEditing(true)}
      className="hover:text-orange-400 font-mono-tech text-sm"
      data-testid={`price-cell-${tool.slug}-${planKey}`}>
      {tool[`price_${planKey}`] != null ? `$${tool[`price_${planKey}`]}` : <span className="text-zinc-600">—</span>}
    </button>
  );
}

export default function ThirdPartyManager() {
  const [tab, setTab] = useState("tools");
  const [tools, setTools] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t, o] = await Promise.all([
        axios.get(`${API}/store/tools?available_only=false`),
        axios.get(`${API}/store/orders`),
      ]);
      setTools(t.data); setOrders(o.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const patchTool = async (id, updates) => {
    await axios.patch(`${API}/store/tools/${id}`, updates);
    await load();
  };

  const patchOrderStatus = async (id, status) => {
    const notes = window.prompt("Note interne (optionnel) :", "") || null;
    await axios.patch(`${API}/store/orders/${id}`, { status, notes });
    await load();
  };

  if (loading) return <div className="flex justify-center p-10"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-4" data-testid="third-party-manager">
      <div className="flex gap-2">
        <button onClick={() => setTab("tools")} data-testid="tpm-tab-tools"
          className={`px-4 py-2 rounded-lg text-sm border flex items-center gap-2 ${tab==="tools" ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-zinc-800 text-zinc-400"}`}>
          <Package className="w-4 h-4" /> Prix outils ({tools.length})
        </button>
        <button onClick={() => setTab("orders")} data-testid="tpm-tab-orders"
          className={`px-4 py-2 rounded-lg text-sm border flex items-center gap-2 ${tab==="orders" ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-zinc-800 text-zinc-400"}`}>
          <ShoppingBag className="w-4 h-4" /> Commandes ({orders.length})
        </button>
      </div>

      {tab === "tools" && (
        <div className="card-lhs p-5 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[10px] uppercase text-zinc-500 font-mono-tech border-b border-zinc-800">
                <th className="pb-2">Logiciel</th>
                <th className="pb-2">3 mois</th>
                <th className="pb-2">6 mois</th>
                <th className="pb-2">12 mois</th>
                <th className="pb-2 text-right">Disponible</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/60">
              {tools.map(t => (
                <tr key={t.id} data-testid={`row-tool-${t.slug}`}>
                  <td className="py-2.5"><div className="font-semibold">{t.name}</div>
                    <div className="text-[10px] text-zinc-500 truncate max-w-xs">{t.description}</div></td>
                  <td className="py-2.5"><PriceCell tool={t} planKey="3m" onSave={patchTool} /></td>
                  <td className="py-2.5"><PriceCell tool={t} planKey="6m" onSave={patchTool} /></td>
                  <td className="py-2.5"><PriceCell tool={t} planKey="12m" onSave={patchTool} /></td>
                  <td className="py-2.5 text-right">
                    <button
                      onClick={() => patchTool(t.id, { available: !t.available })}
                      data-testid={`toggle-tool-${t.slug}`}
                      className={`text-[11px] px-2 py-1 rounded-full border font-mono-tech ${
                        t.available ? "text-emerald-400 border-emerald-500/40" : "text-zinc-500 border-zinc-700"
                      }`}>
                      {t.available ? "OUI" : "NON"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-2" data-testid="tpm-orders-list">
          {orders.length === 0 && <div className="card-lhs p-5 text-sm text-zinc-500 italic">Aucune commande.</div>}
          {orders.map(o => {
            const canValidate = ["pending", "pending_payment_validation"].includes(o.status);
            const canActivate = ["payment_validated", "paid", "pending"].includes(o.status);
            const canRefuse = ["pending", "pending_payment_validation"].includes(o.status);
            return (
              <div key={o.id} className="card-lhs p-4" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap gap-3 justify-between items-start mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono-tech text-orange-400 text-xs">{o.order_number}</div>
                    <div className="font-semibold text-base">{o.tool_name} — {o.plan.toUpperCase()}</div>
                    <div className="text-[11px] text-zinc-400 mt-0.5">{new Date(o.created_at).toLocaleString("fr-FR")}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border font-mono-tech shrink-0 ${STATUS_COLOR[o.status]}`}>
                    {STATUS_OPTS.find(s => s.v === o.status)?.label || o.status}
                  </span>
                </div>

                {/* Dossier admin — tous les champs du cahier */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs mb-3">
                  <div><span className="text-zinc-500">Technicien LHSunlocker : </span><b>{o.user_name}</b></div>
                  <div><span className="text-zinc-500">E-mail LHSunlocker : </span><span className="font-mono-tech">{o.user_email}</span></div>
                  <div><span className="text-zinc-500">Compte logiciel tiers : </span><b className="text-orange-400">{o.tp_username || "—"}</b></div>
                  <div><span className="text-zinc-500">E-mail compte tiers : </span><span className="font-mono-tech">{o.tp_email || "—"}</span></div>
                  <div><span className="text-zinc-500">Prix : </span><b className="text-orange-400">{o.price} {o.currency}</b></div>
                  <div><span className="text-zinc-500">Mode paiement : </span><b>{(o.payment_method || "—").toUpperCase()}</b></div>
                  <div className="sm:col-span-2"><span className="text-zinc-500">Référence paiement : </span><span className="font-mono-tech">{o.payment_reference || "—"}</span></div>
                  {o.payment_validated && (
                    <div className="sm:col-span-2 text-emerald-400">✅ Paiement validé par {o.payment_validated_by_name || "admin"} le {o.payment_validated_at && new Date(o.payment_validated_at).toLocaleString("fr-FR")}</div>
                  )}
                  {o.activation_ref && (
                    <div className="sm:col-span-2 text-emerald-400 font-mono-tech">
                      ✓ Activé : {o.activation_ref} · Expire : {o.expires_at && new Date(o.expires_at).toLocaleDateString("fr-FR")}
                    </div>
                  )}
                  {o.notes && <div className="sm:col-span-2 italic text-zinc-400">Note : {o.notes}</div>}
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  {canValidate && (
                    <button onClick={() => patchOrderStatus(o.id, "payment_validated")}
                      data-testid={`admin-validate-${o.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-sky-500/20 text-sky-300 border border-sky-500/40 hover:bg-sky-500/30">
                      ✅ Valider le paiement
                    </button>
                  )}
                  {canRefuse && (
                    <button onClick={() => patchOrderStatus(o.id, "payment_rejected")}
                      data-testid={`admin-refuse-${o.id}`}
                      className="text-xs px-3 py-1.5 rounded-lg bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30">
                      ❌ Refuser
                    </button>
                  )}
                  {canActivate && (
                    <button onClick={() => patchOrderStatus(o.id, "activated")}
                      data-testid={`admin-activate-${o.id}`}
                      className="btn-orange text-xs px-3 py-1.5 rounded-lg">
                      ✅ Activer la licence
                    </button>
                  )}
                  <select
                    data-testid={`order-status-${o.id}`}
                    defaultValue=""
                    onChange={(e) => { if (e.target.value) patchOrderStatus(o.id, e.target.value); e.target.value=""; }}
                    className="input-lhs !py-1 !text-xs">
                    <option value="">Autre statut…</option>
                    {STATUS_OPTS.filter(s => s.v !== o.status).map(s => (
                      <option key={s.v} value={s.v}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
